import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Container, Image, Center, Box, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';

import { Button, LayoutPublic, PasswordInput, Text, TextInput } from "components";
import { AppConstants, RouteConstants, ServiceConstants } from 'constant';
import { PublicService } from 'services';
import { AppUtils, StorageUtils } from 'utils';

import { IRootState } from 'config/redux';

import logo from 'assets/logo.svg';
import * as Validations from './validation';
import { UserResponse } from 'types';

const { STORAGE_USER } = AppConstants;
const { ROUTE_DASHBOARD } = RouteConstants;
const { REQUEST_PENDING, REQUEST_LOGIN_ID } = ServiceConstants;

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_LOGIN_ID]);

  const [user] = useState<UserResponse | undefined>(StorageUtils.getObject(STORAGE_USER))

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value: string) => Validations.validateEmail(value, t),
      password: (value: string) => Validations.validatePassword(value, t)
    },
  });

  useEffect(() => {
    if (user) {
      navigate(ROUTE_DASHBOARD)
    }
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      serviceObject,
      REQUEST_LOGIN_ID,
      (val) => navigate(ROUTE_DASHBOARD),
      (val) => form.setFieldError('email', t('login.error.credentials'))
  );
  }, [serviceObject]);

  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  const login = useCallback(async () => {
    const values = form.getValues();
    await PublicService.login(values.email, values.password);
  }, [form]);

  return (
    <LayoutPublic>
      <Container
        h={'80vh'}
        display={'flex'}
        style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
      >
        <Box mt={"xl"} w={400}>
          <Center>
            <Image src={logo} w={180} fallbackSrc={logo} />
          </Center>
          <Text style={{ fontSize: 24, textAlign: 'center' }}>{'Webkredit.ch'}</Text>
          <Title fw={500} ta={'center'} my={'lg'} mt={40}>{t('login.title')}</Title>
          <Box component="form" mt={40} onSubmit={form.onSubmit(login)}>
            <TextInput
              label={t('login.email.title')}
              placeholder={t('login.email.placeholder')}
              required={true}
              disabled={isLoading()}
              key={form.key('email')}
              {...form.getInputProps('email')}
            />
            <PasswordInput
              label={t('login.password.title')}
              placeholder={t('login.password.placeholder')}
              required={true}
              mt={'md'}
              disabled={isLoading()}
              key={form.key('password')}
              {...form.getInputProps('password')}
            />
            <Box mt={40}>
              <Button
                fullWidth={true}
                type="submit"
                disabled={!form.isValid() || isLoading()}
                loading={isLoading()}
              >
                {t('login.login')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </LayoutPublic>
  )
}

export default LoginPage;
